import { useState, useRef, useEffect, FocusEvent } from 'react';

import { Box, Drawer } from '@mui/material';
import cx from 'classnames';
import { useHotkeys } from 'react-hotkeys-hook';

import { SidebarContent } from './components/SidebarContent';
import styles from './Sidebar.module.scss';
import { IconButton } from '@/components/ui/IconButton';
import { uiIcons } from '@/constants/uiIcons';
import { useMobileNavigationStore } from '@/stores/mobileNavigation';
import { useNavigationStore } from '@/stores/navigation';

/**
 * Navigation Props Interface
 *
 * Changed from RefObject to callback ref pattern to support SkipLinks race condition fix.
 * The parent component (DefaultLayout) passes a callback function that gets called
 * when this Sidebar component mounts/unmounts.
 */
interface NavigationProps {
  /** Callback ref function called when the sidebar element mounts/unmounts */
  ref: (element: HTMLDivElement | null) => void;
}

export const Sidebar = ({ ref }: NavigationProps) => {
  const { isCollapsed } = useNavigationStore();
  const { setMobileOpen, mobileOpen, setIsClosing } =
    useMobileNavigationStore();
  const [isHovered, setIsHovered] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);

  const hoverTimeoutRef = useRef<number | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      window.clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    // Add a delay before hiding the sidebar
    hoverTimeoutRef.current = window.setTimeout(() => {
      setIsHovered(false);
    }, 500);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        window.clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Handle escape key to hide sidebar when collapsed and hovered
  useHotkeys(
    'esc',
    () => {
      if (isCollapsed && (isHovered || hasFocus)) {
        // Clear hover state
        setIsHovered(false);
        if (hoverTimeoutRef.current) {
          window.clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }

        // Remove focus from any focused element in the sidebar
        if (sidebarRef.current?.contains(document.activeElement)) {
          (document.activeElement as HTMLElement)?.blur();
        }
      }
    },
    {
      enableOnFormTags: true,
      preventDefault: true,
    },
  );

  // Track focus state for keyboard navigation
  const handleFocusIn = () => {
    setHasFocus(true);
  };

  const handleFocusOut = (event: FocusEvent) => {
    // Check if focus is moving outside the sidebar
    if (!sidebarRef.current?.contains(event.relatedTarget as Node)) {
      setHasFocus(false);
    }
  };

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  return (
    <>
      <Drawer
        id="mobile-navigation-drawer"
        variant="temporary"
        open={mobileOpen}
        anchor="right"
        onTransitionEnd={handleDrawerTransitionEnd}
        onClose={handleDrawerClose}
        sx={{
          display: { xs: 'inherit', md: 'none' },
        }}
        slotProps={{
          root: {
            keepMounted: true,
          },
        }}
      >
        <div className={styles.SidebarMobileDrawerHeader}>
          <IconButton
            onClick={handleDrawerClose}
            iconName={uiIcons.actions.close}
            tooltip="Close menu"
          />
        </div>
        <SidebarContent />
      </Drawer>
      <Box
        sx={{
          display: { xs: 'none', md: 'inherit' },
        }}
        className={cx(styles.DesktopDrawer, {
          [styles.IsCollapsed]: isCollapsed,
          [styles.IsCollapsedVisible]: isCollapsed && isHovered,
        })}
        onMouseEnter={isCollapsed ? handleMouseEnter : undefined}
        onMouseLeave={isCollapsed ? handleMouseLeave : undefined}
        onFocusCapture={isCollapsed ? handleFocusIn : undefined}
        onBlurCapture={isCollapsed ? handleFocusOut : undefined}
        ref={(node) => {
          // Store ref locally for our own use (focus management, etc.)
          sidebarRef.current = node as HTMLDivElement;
          // Call the callback ref passed from parent (for SkipLinks integration)
          ref(node as HTMLDivElement);
        }}
      >
        <SidebarContent />
      </Box>
    </>
  );
};
